import React, { useMemo, useCallback, useState } from "react";
import { Animate } from "react-show";
import dayjs from "dayjs";
import _ from "lodash";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import styled from "styled-components";
import media from "styled-media-query";
import { NotificationEntity } from "src/firebase/db/Notifications";
import {
  OrderBreadProps,
  orderBreadInitialEntity,
} from "src/firebase/db/OrderBreads";

import {
  formatDate,
  formatDateToDate,
  formatDateAsQuery,
} from "src/helpers/UtilityHelper";
import StyleConfig from "src/constants/StyleConfig";
import { LinkButton } from "src/components/atoms/button/Button";

dayjs.extend(isSameOrBefore);

type Props = {
  notification: NotificationEntity;
};

const ReservationOrderListItem = (props: Props) => {
  const {
    notification: { id, last_pickup_days, order_breads },
  } = props;
  const [showDetail, setShowDetail] = useState(false);

  // 通知詳細
  const orderBread = useMemo(() => {
    if (!order_breads.length) {
      return {
        ...orderBreadInitialEntity,
        original_orders: [],
      };
    }
    return order_breads[0] as OrderBreadProps;
  }, [order_breads]);

  // 受け取り日時
  const lastPickupDays = useMemo(() => {
    return last_pickup_days as (Date | undefined)[];
  }, [last_pickup_days]);

  const { name, stock, orders } = orderBread;

  const totalSoldQuntity = useMemo(() => {
    const quantities = orders
      .filter(({ status }) => status === "succeeded")
      .map(({ items }) => items.map(({ quantity }) => quantity));
    return _(quantities).flatten().sum();
  }, [orders]);

  const dailySoldQuantity = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        return "";
      }
      // 対象日付に購入されたデータのみ参照する
      const dailyOrders = orders.filter((v) => {
        const lastPickupDate = dayjs(formatDateToDate(v.last_pickup_time));
        const d = dayjs(formatDateToDate(date)).format("YYYY-MM-DD");
        return lastPickupDate.isSame(d, "day") && v.status === "succeeded";
      });
      return _.sumBy(dailyOrders, "quantity");
    },
    [orders]
  );

  return (
    <ListItem>
      <div className="detail">
        <div className="detail-header">
          <div className="title">{name}</div>
          <div className="status">
            <div className="count">
              <span>現時点受注数：</span>
              <span>
                {totalSoldQuntity}個／{stock}個
              </span>
            </div>
            {totalSoldQuntity > 0 && (
              <button onClick={() => setShowDetail(!showDetail)}>
                {showDetail ? "↑詳細を閉じる" : "↓詳細を見る"}
              </button>
            )}
          </div>
        </div>
        <Animate
          show={showDetail}
          duration={500}
          style={{
            height: "auto",
            background: "white",
            overflow: "hidden",
          }}
          start={{
            opacity: 0,
            height: 0,
          }}
          enter={{
            opacity: 1,
            height: "auto",
          }}
          leave={{
            opacity: 0,
            height: 0,
          }}
        >
          <div className="detail-body">
            <h4>
              <span>受け渡し日</span>
              <span>注文状況</span>
              <span>日別注文詳細</span>
            </h4>
            <ul>
              {lastPickupDays.map((v, i) => (
                <li key={`${id}|${i}`}>
                  <span>{formatDate(v, "YYYY年MM月DD日(ddd)")}</span>
                  <span>{dailySoldQuantity(v)}個</span>
                  <span>
                    <LinkButton
                      isDisabled={dailySoldQuantity(v) < 1}
                      buttonColor="green"
                      url={`/reservations/orders/${id}?date=${formatDateAsQuery(
                        v
                      )}`}
                      text="注文詳細を見る"
                    />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Animate>
      </div>
    </ListItem>
  );
};

const ListItem = styled.div`
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  line-height: ${20 / 16};

  .detail {
    background-color: #fff;
    margin-bottom: 32px;
    width: 100%;
    padding: 20px;

    .detail-header {
      margin-bottom: 24px;

      .title {
        font-size: 20px;
        font-weight: bold;
        margin-bottom: 10px;
      }

      .status {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .count {
          font-size: 14px;
          span {
            &:first-child {
              color: ${StyleConfig.COLOR.TEXT_GRAY};
            }
            &:last-child {
              font-weight: bold;
            }
          }
        }
      }
    }

    .detail-body {
      h4,
      ul li {
        width: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: space-between;

        > span {
          font-size: 12px;
          ${media.greaterThan("large")`
            font-size: 14px;
          `};

          &:first-child {
            width: 120px;
            ${media.greaterThan("large")`
              width: 200px;
            `};
          }

          &:nth-child(2) {
            width: 50px;
            ${media.greaterThan("large")`
              width: 100px;
            `};
          }

          &:last-child {
            width: 110px;
            ${media.greaterThan("large")`
              width: 160px;
            `};
          }
        }
      }

      h4 {
        font-size: 12px;
        font-weight: bold;
        margin-bottom: 12px;
      }

      ul {
        li {
          position: relative;
          color: ${StyleConfig.COLOR.GREEN};
          padding: 8px 0;
          border-top: 1px solid #bababa;

          &:last-child {
            border-bottom: 1px solid #bababa;
          }

          span {
            color: ${StyleConfig.COLOR.TEXT_COLOR};
          }
        }
      }
    }
  }
`;

export default ReservationOrderListItem;
